import { IScenarioInfo } from 'components/SharedScenarioItem/SharedScenarioItem'
import AdaptiveTabs from 'components/AdaptiveTabs/AdaptiveTabs'
import { StyledTab } from 'components/StyledTab/StyledTab'
import TabPanel from 'components/TabPanel/TabPanel'
import type { NormalizedStatisticsApiRow } from 'features/tasksNormalized/filterableRecord'
import { useIsMobile } from 'hooks/useIsMobile'
import { canSeeRobotTasksTab, useUser } from 'hooks/user'
import GausiumScenariosTabContent from 'pages/Facility/FacilityView/GausiumScenariosTabContent/GausiumScenariosTabContent'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { selectRobot } from 'store/selectors/robot'
import { GAUSIUM_ENABLED } from 'utils/constants'
import { canSeeServiceReport } from 'utils/serviceReportForm'
import { VENDOR, type VendorCode } from 'utils/vendorDisplayName'

import RobotControl from './RobotControl/RobotControl'
import RobotDetails from './RobotDetails/RobotDetails'
import RobotHistory from './RobotHistory/RobotHistory'
import RobotTasks from './RobotTasks/RobotTasks'
import AsbisServiceControl from './Service/AsbisServiceControl'
import GausiumServiceControl from './Service/GausiumServiceControl'
import Service from './Service/Service'
import type { ServiceCalibCatalogProps } from './Service/ServiceCalibCatalog'

export function getRobotTabIndices(
  vendor: VendorCode | string | undefined,
  gaussiumEnabled: boolean,
  showTasksTab: boolean,
  showServiceTab = true
) {
  const isAsbis = vendor === VENDOR.ASBIS
  const isGausium = vendor === VENDOR.GAUSIUM
  const showGausiumScenariosTab = isGausium && gaussiumEnabled

  if (isAsbis) {
    return {
      detailsIndex: 0,
      controlIndex: -1,
      serviceIndex: 1,
      scenariosIndex: -1,
      historyIndex: -1,
      tasksIndex: -1,
      showGausiumScenariosTab: false,
      isAsbis: true,
    }
  }

  if (!showServiceTab) {
    return {
      detailsIndex: 0,
      controlIndex: 1,
      serviceIndex: -1,
      scenariosIndex: showGausiumScenariosTab ? 2 : -1,
      historyIndex: showGausiumScenariosTab ? 3 : 2,
      tasksIndex: -1,
      showGausiumScenariosTab,
      isAsbis: false,
    }
  }

  return {
    detailsIndex: 0,
    controlIndex: 1,
    serviceIndex: 2,
    scenariosIndex: showGausiumScenariosTab ? 3 : -1,
    historyIndex: showGausiumScenariosTab ? 4 : 3,
    tasksIndex: showTasksTab && !isGausium ? 4 : -1,
    showGausiumScenariosTab,
    isAsbis: false,
  }
}

export type RobotTabName = 'details' | 'control' | 'service' | 'scenarios' | 'history' | 'tasks'

export type RobotTabIndices = ReturnType<typeof getRobotTabIndices>

export function robotTabNameFromIndex(index: number, indices: RobotTabIndices): RobotTabName {
  if (index === indices.controlIndex) return 'control'
  if (index === indices.serviceIndex) return 'service'
  if (index === indices.scenariosIndex) return 'scenarios'
  if (index === indices.historyIndex) return 'history'
  if (index === indices.tasksIndex) return 'tasks'
  return 'details'
}

export function robotTabIndexFromName(name: string | null | undefined, indices: RobotTabIndices): number {
  const normalized = String(name || '')
    .trim()
    .toLowerCase()
  const byName: Record<string, number> = {
    details: indices.detailsIndex,
    control: indices.controlIndex,
    service: indices.serviceIndex,
    scenarios: indices.scenariosIndex,
    history: indices.historyIndex,
    tasks: indices.tasksIndex,
  }
  const index = byName[normalized]
  if (index == null || index < 0) return indices.detailsIndex
  return index
}

export type RobotLeftBlockGausiumScenariosProps = {
  highlightedScenarioId?: number
  onScenarioClick: (id: number, missionIds: Array<number | string>) => void
  onSyncScenarioState: (scenarios: IScenarioInfo[], totalCount: number) => void
}

type RobotLeftBlockProps = {
  tab: number
  onTabChange: (event: React.SyntheticEvent, newValue: number) => void
  gausiumScenarios?: RobotLeftBlockGausiumScenariosProps
  onHistoryRowClick?: (row: NormalizedStatisticsApiRow) => void
  displayOffsetMinutes?: number
  serviceCalib?: Omit<ServiceCalibCatalogProps, 'isOnline'>
}

const RobotLeftBlock = ({
  tab,
  onTabChange,
  gausiumScenarios,
  onHistoryRowClick,
  displayOffsetMinutes,
  serviceCalib,
}: RobotLeftBlockProps) => {
  const { t } = useTranslation()
  const user = useUser()
  const robot = useSelector(selectRobot)
  const isMobile = useIsMobile()
  const vendor = robot.robotInfo?.vendor
  const isGausium = vendor === VENDOR.GAUSIUM
  const isAsbis = vendor === VENDOR.ASBIS

  const showTasksTab = !isGausium && !isAsbis && canSeeRobotTasksTab(user?.state ?? { audience: null })
  const showServiceTab =
    !isGausium || canSeeServiceReport({ audience: user?.state.audience ?? null, vendor })

  const {
    detailsIndex,
    controlIndex,
    serviceIndex,
    scenariosIndex,
    historyIndex,
    tasksIndex,
    showGausiumScenariosTab,
  } = getRobotTabIndices(vendor, GAUSIUM_ENABLED, showTasksTab, showServiceTab)

  const gausiumRobotIds = useMemo(
    () => (robot.robotInfo?.id != null ? [robot.robotInfo.id] : []),
    [robot.robotInfo?.id]
  )

  if (isAsbis) {
    return (
      <div className="flex-column">
        <AdaptiveTabs
          value={tab}
          onChange={onTabChange}
          indicatorColor="primary"
          sx={{ height: 45, minHeight: 45 }}
        >
          <StyledTab
            label={t('robot.details')}
            sx={{ fontSize: 16, fontWeight: '400', textTransform: 'none', padding: '12px 10px' }}
            disableRipple
          />
          <StyledTab
            label={t('robot.service')}
            sx={{ fontSize: 16, fontWeight: '400', textTransform: 'none', padding: '12px 10px' }}
            disableRipple
          />
        </AdaptiveTabs>
        <TabPanel value={tab} index={detailsIndex} disablePadding={true}>
          <RobotDetails />
        </TabPanel>
        <TabPanel value={tab} index={serviceIndex} disablePadding={true}>
          <AsbisServiceControl />
        </TabPanel>
      </div>
    )
  }

  return (
    <div className="flex-column">
      <AdaptiveTabs
        value={tab}
        onChange={onTabChange}
        indicatorColor="primary"
        sx={{ height: 45, minHeight: 45 }}
      >
        <StyledTab
          label={t('robot.details')}
          sx={{ fontSize: 16, fontWeight: '400', textTransform: 'none', padding: '12px 10px' }}
          disableRipple
        />
        <StyledTab
          label={t('robot.control')}
          sx={{ fontSize: 16, fontWeight: '400', textTransform: 'none', padding: '12px 10px' }}
          disableRipple
        />
        {showServiceTab && (
          <StyledTab
            label={t('robot.service')}
            sx={{ fontSize: 16, fontWeight: '400', textTransform: 'none', padding: '12px 10px' }}
            disableRipple
          />
        )}
        {showGausiumScenariosTab && (
          <StyledTab
            label="Scenarios"
            sx={{ fontSize: 16, fontWeight: '400', textTransform: 'none', letterSpacing: 0, padding: '12px 10px' }}
            disableRipple
          />
        )}
        <StyledTab
          label={t('robot.history')}
          sx={{ fontSize: 16, fontWeight: '400', textTransform: 'none', padding: '12px 10px' }}
          disableRipple
        />
        {showTasksTab && (
          <StyledTab
            label={t('robot.tasks')}
            sx={{ fontSize: 16, fontWeight: '400', textTransform: 'none', padding: '12px 10px' }}
            disableRipple
          />
        )}
      </AdaptiveTabs>
      <TabPanel value={tab} index={detailsIndex} disablePadding={true} scrollable={true}>
        <RobotDetails />
      </TabPanel>
      <TabPanel value={tab} index={controlIndex} disablePadding={true} scrollable={true}>
        <RobotControl />
      </TabPanel>
      {showServiceTab && (
        <TabPanel value={tab} index={serviceIndex} disablePadding={true} scrollable={true}>
          {isGausium ? <GausiumServiceControl /> : <Service serviceCalib={serviceCalib} />}
        </TabPanel>
      )}
      {showGausiumScenariosTab && (
        <TabPanel value={tab} index={scenariosIndex} disablePadding={true}>
          <GausiumScenariosTabContent
            gausiumRobotIds={gausiumRobotIds}
            highlightedScenarioId={gausiumScenarios?.highlightedScenarioId}
            onScenarioClick={gausiumScenarios?.onScenarioClick}
            onSyncScenarioState={gausiumScenarios?.onSyncScenarioState}
            hideRobotColumn={true}
            hideIdColumn={isMobile}
          />
        </TabPanel>
      )}
      <TabPanel value={tab} index={historyIndex} disablePadding={true}>
        <RobotHistory onRowClick={onHistoryRowClick} displayOffsetMinutes={displayOffsetMinutes} />
      </TabPanel>
      {showTasksTab && (
        <TabPanel value={tab} index={tasksIndex} disablePadding={true}>
          <RobotTasks displayOffsetMinutes={displayOffsetMinutes} />
        </TabPanel>
      )}
    </div>
  )
}

export default RobotLeftBlock
